import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle,
  ExternalLink,
  Inbox,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  UserRound,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import PeopleWorkspaceShell from '../../components/people/PeopleWorkspaceShell';
import PersonMessageComposer from '../../components/portal/PersonMessageComposer';
import WhatsAppAttachment from '../../components/portal/WhatsAppAttachment';
import WorkspaceLink from '../../components/workspace/WorkspaceLink';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import API_BASE from '../../utils/apiBase';
import {
  filterStaffCommunicationsHubRows,
  staffMessageDirectionLabel,
  staffMessageState,
  summarizeStaffCommunications,
  summarizeStaffCommunicationsHub,
} from './staffCommunicationsHelpers';

const API = API_BASE;

function formatDateTime(value) {
  if (!value) return 'No contact yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function safeDetail(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}

function statusTone(status) {
  if (status === 'active') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status === 'onboarding') return 'border-blue-200 bg-blue-50 text-blue-800';
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

function StaffRow({ row, selected, onSelect }) {
  const unread = Number(row?.unread_count || 0);
  const lastChannel = String(row?.channel || 'email').toLowerCase();
  return (
    <button
      type="button"
      onClick={() => onSelect(row.employee_id)}
      className={`w-full border-b border-slate-100 px-4 py-3 text-left transition ${
        selected ? 'bg-primary/5 shadow-[inset_3px_0_0_var(--color-primary,#0d4f3c)]' : 'bg-white hover:bg-slate-50'
      }`}
      data-testid={`staff-hub-row-${row.employee_id}`}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-2 h-2.5 w-2.5 shrink-0 rounded-full ${unread > 0 ? 'bg-primary' : 'bg-slate-300'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <p className={`truncate text-sm ${unread > 0 ? 'font-bold text-slate-950' : 'font-semibold text-slate-800'}`}>
              {row.display_name}
            </p>
            {unread > 0 ? (
              <Badge className="shrink-0 bg-primary text-white hover:bg-primary">{unread}</Badge>
            ) : null}
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {row.employee_code || 'Employee'} · {row.contact_email || 'No staff email'}
          </p>
          <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-600">
            {row.last_preview || 'No staff messages yet.'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={statusTone(row.status)}>{row.status}</Badge>
            {row.has_history ? (
              <Badge variant="outline" className={lastChannel === 'whatsapp' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-700'}>
                {lastChannel === 'whatsapp' ? 'WhatsApp' : 'Email'}
              </Badge>
            ) : null}
            <span className="text-[11px] text-slate-500">{formatDateTime(row.last_message_at)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function StaffCommunicationsHubPage() {
  const { token, isManagerOrAdmin } = useAuth();
  const managerAccess = isManagerOrAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEmployeeId = searchParams.get('employee');

  const [hubPayload, setHubPayload] = useState(null);
  const [hubLoading, setHubLoading] = useState(true);
  const [hubError, setHubError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('current');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [channelFilter, setChannelFilter] = useState('all');

  const [conversationPayload, setConversationPayload] = useState(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadHub = useCallback(async ({ silent = false } = {}) => {
    if (!token || !managerAccess) return;
    if (!silent) setHubLoading(true);
    setHubError('');
    try {
      const response = await axios.get(`${API}/staff-communications/hub`, { headers });
      setHubPayload(response.data || null);
    } catch (error) {
      setHubError(safeDetail(error, 'Staff communications hub could not be loaded'));
    } finally {
      if (!silent) setHubLoading(false);
    }
  }, [headers, managerAccess, token]);

  const loadConversation = useCallback(async (employeeId) => {
    if (!employeeId || !token || !managerAccess) return;
    setConversationLoading(true);
    setConversationError('');
    setConversationPayload(null);
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/staff-communications`, { headers });
      setConversationPayload(response.data || null);
      await loadHub({ silent: true });
    } catch (error) {
      setConversationError(safeDetail(error, 'Staff conversation could not be loaded'));
    } finally {
      setConversationLoading(false);
    }
  }, [headers, loadHub, managerAccess, token]);

  useEffect(() => {
    loadHub();
  }, [loadHub]);

  useEffect(() => {
    setConversationPayload(null);
    setConversationError('');
    if (selectedEmployeeId) loadConversation(selectedEmployeeId);
  }, [loadConversation, selectedEmployeeId]);

  const hub = summarizeStaffCommunicationsHub(hubPayload);
  const conversation = summarizeStaffCommunications(conversationPayload);
  const filteredRows = useMemo(
    () => filterStaffCommunicationsHubRows(hub.rows, {
      query,
      status: statusFilter,
      unreadOnly,
      channel: channelFilter,
    }),
    [channelFilter, hub.rows, query, statusFilter, unreadOnly],
  );

  const selectEmployee = (employeeId) => {
    const next = new URLSearchParams(searchParams);
    next.set('employee', employeeId);
    setSearchParams(next);
  };

  const clearSelection = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('employee');
    setSearchParams(next);
  };



  if (!managerAccess) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-600">
        Staff Communications is restricted to managers and administrators.
      </div>
    );
  }

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      {hub.summary?.total_unread > 0 ? (
        <Badge className="bg-primary text-white hover:bg-primary">{hub.summary.total_unread} unread</Badge>
      ) : null}
      <Badge variant="outline" className={hub.whatsappEnabled ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-700'}>
        {hub.whatsappEnabled ? 'WhatsApp support connected' : 'WhatsApp not configured'}
      </Badge>
      <Button type="button" variant="outline" size="sm" onClick={() => loadHub()} disabled={hubLoading}>
        <RefreshCw className={`mr-1.5 h-4 w-4 ${hubLoading ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>
  );

  return (
    <PeopleWorkspaceShell
      activeView="communications"
      title="Staff communications"
      description="Start personal email or WhatsApp support messages from one shared composer. Recruitment conversations remain separate and are never shown here."
      actions={actions}
    >
      <div className="grid min-h-[640px] gap-4 lg:grid-cols-[360px_minmax(0,1fr)]" data-testid="staff-communications-hub">
        <section className={`${selectedEmployeeId ? 'hidden lg:flex' : 'flex'} min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:max-h-[calc(100vh-235px)]`}>
          <div className="shrink-0 border-b border-slate-200 p-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-emerald-50 px-2 py-2">
                <p className="text-lg font-bold text-emerald-900">{hub.summary?.active || 0}</p>
                <p className="text-[11px] text-emerald-700">Active</p>
              </div>
              <div className="rounded-lg bg-blue-50 px-2 py-2">
                <p className="text-lg font-bold text-blue-900">{hub.summary?.onboarding || 0}</p>
                <p className="text-[11px] text-blue-700">Onboarding</p>
              </div>
              <div className="rounded-lg bg-slate-100 px-2 py-2">
                <p className="text-lg font-bold text-slate-900">{hub.summary?.inactive || 0}</p>
                <p className="text-[11px] text-slate-600">Inactive</p>
              </div>
            </div>

            <label className="mt-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <Search className="h-4 w-4 text-slate-400" />
              <span className="sr-only">Search staff communications</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search staff, email or message"
                className="w-full bg-transparent text-sm outline-none"
              />
            </label>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current staff</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="all">All staff</SelectItem>
                </SelectContent>
              </Select>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <label className="mt-3 flex items-center gap-2 text-xs font-medium text-slate-600">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(event) => setUnreadOnly(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Unread conversations only
            </label>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {hubLoading ? (
              <div className="flex min-h-[320px] items-center justify-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading staff
              </div>
            ) : hubError ? (
              <div className="p-6 text-center text-sm text-red-700">{hubError}</div>
            ) : filteredRows.length === 0 ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center px-6 text-center text-slate-500">
                <Inbox className="mb-3 h-10 w-10 text-slate-300" />
                <p className="font-semibold text-slate-800">No staff match these filters</p>
                <p className="mt-1 text-xs">Active and onboarding staff appear even before their first message.</p>
              </div>
            ) : filteredRows.map((row) => (
              <StaffRow
                key={row.employee_id}
                row={row}
                selected={selectedEmployeeId === row.employee_id}
                onSelect={selectEmployee}
              />
            ))}
          </div>
        </section>

        <section className={`${selectedEmployeeId ? 'flex' : 'hidden lg:flex'} min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm lg:max-h-[calc(100vh-235px)]`}>
          {!selectedEmployeeId ? (
            <div className="flex min-h-[520px] flex-1 flex-col items-center justify-center p-8 text-center text-slate-500">
              <Mail className="mb-3 h-12 w-12 text-slate-300" />
              <p className="font-semibold text-slate-900">Select a staff member</p>
              <p className="mt-1 max-w-md text-sm">Their staff-domain email and approved WhatsApp history will open here.</p>
            </div>
          ) : conversationLoading ? (
            <div className="flex min-h-[520px] flex-1 items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading conversation
            </div>
          ) : conversationError ? (
            <div className="flex min-h-[520px] flex-1 flex-col items-center justify-center p-8 text-center">
              <AlertTriangle className="mb-3 h-10 w-10 text-amber-600" />
              <p className="font-semibold text-slate-900">Conversation unavailable</p>
              <p className="mt-1 text-sm text-slate-600">{conversationError}</p>
              <Button type="button" variant="outline" className="mt-4" onClick={clearSelection}>Back to staff list</Button>
            </div>
          ) : (
            <>
              <div className="shrink-0 border-b border-slate-200 bg-white p-4">
                <Button type="button" variant="ghost" size="sm" onClick={clearSelection} className="mb-2 -ml-2 lg:hidden">
                  Back to staff list
                </Button>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <UserRound className="h-5 w-5 text-primary" />
                      <h2 className="text-lg font-semibold text-slate-950">{conversation.employee?.display_name || 'Employee'}</h2>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      {conversation.employee?.employee_code || 'Employee'} · {conversation.employee?.status || 'staff'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild variant="outline" size="sm">
                      <WorkspaceLink to={`/portal/employees/${selectedEmployeeId}`}>
                        Staff profile
                      </WorkspaceLink>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <WorkspaceLink to={`/portal/employees/${selectedEmployeeId}/communications`}>
                        <ExternalLink className="mr-1.5 h-4 w-4" /> Full view
                      </WorkspaceLink>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {conversation.messages.length === 0 ? (
                  <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
                    No staff messages yet. Recruitment history is intentionally not shown here.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {conversation.messages.map((message) => {
                      const inbound = String(message?.direction || '').toLowerCase() === 'inbound';
                      const whatsapp = String(message?.channel || '').toLowerCase() === 'whatsapp';
                      const state = staffMessageState(message);
                      return (
                        <div key={message.id} className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
                          <article className={`w-full max-w-[760px] rounded-xl border p-4 ${inbound ? 'border-slate-200 bg-white' : 'border-primary/20 bg-primary/5'}`}>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                              <Badge
                                variant="outline"
                                className={
                                  whatsapp
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                    : inbound
                                      ? 'border-blue-200 bg-blue-50 text-blue-800'
                                      : 'border-slate-200 bg-slate-50 text-slate-700'
                                }
                              >
                                {staffMessageDirectionLabel(message)}
                              </Badge>
                              <span>{state.label}</span>
                              <span>·</span>
                              <span>{formatDateTime(message.created_at)}</span>
                            </div>
                            <p className="mt-2 font-semibold text-slate-900">{message.subject || '(no subject)'}</p>
                            <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{message.body_text || ''}</p>
                            <WhatsAppAttachment message={message} />
                          </article>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="shrink-0 max-h-[520px] overflow-y-auto border-t border-slate-200 bg-white p-4">
                <PersonMessageComposer
                  domain="staff"
                  employeeId={selectedEmployeeId}
                  token={token}
                  messages={conversation.messages}
                  threadId={conversation.thread?.id || null}
                  emailEnabled={conversation.emailEnabled}
                  emailReason={conversation.emailReason || conversation.readOnlyReason}
                  readOnly={conversation.readOnly}
                  onSent={() => loadConversation(selectedEmployeeId)}
                />
              </div>
            </>
          )}
        </section>
      </div>
    </PeopleWorkspaceShell>
  );
}
